import { useEffect, useState } from "react";
import axios from "axios";
import { Anchor, Compass, Ship, ArrowLeft } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { API } from "@/lib/config";
import { StepBar } from "./fields";
import { VerifyEmail } from "./VerifyEmail";
import { Disclosure } from "./Disclosure";
import { CaptainForm } from "./CaptainForm";
import { FirstMateForm } from "./FirstMateForm";
import { YachtForm } from "./YachtForm";

const TYPE_KEY = "cc_apply_type";
const TYPES = [
  { id: "captain", title: "CAPTAIN", desc: "USCG-licensed. You take the helm.", icon: Compass },
  { id: "first_mate", title: "FIRST MATE", desc: "Crew, galley, cockpit, community.", icon: Anchor },
  { id: "yacht", title: "YACHT / OWNER", desc: "100 ft+, cockpit for 15+. Host the show.", icon: Ship },
];
const FORMS = { captain: CaptainForm, first_mate: FirstMateForm, yacht: YachtForm };
const STEPS = ["Verify", "Disclosure", "Application"];

export const ApplyModal = ({ open, onClose, member, headers, signIn, setMember, onCreated }) => {
  const [type, setType] = useState(() => localStorage.getItem(TYPE_KEY) || "");
  const [options, setOptions] = useState(null);

  useEffect(() => {
    if (open && !options) axios.get(`${API}/applications/options`).then((r) => { if (Array.isArray(r.data?.license_levels)) setOptions(r.data); }).catch(() => {});
  }, [open, options]);

  const pick = (id) => { localStorage.setItem(TYPE_KEY, id); setType(id); };
  const back = () => { localStorage.removeItem(TYPE_KEY); setType(""); };
  const step = !member ? 0 : !member.disclosure_accepted ? 1 : 2;
  const Form = FORMS[type];
  const label = TYPES.find((t) => t.id === type)?.title;

  const created = (applicant) => { onCreated(applicant); back(); onClose(); };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="bg-[#0B111B] border border-[#D4AF37]/30 text-[#F7F8F9] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="apply-modal">
        <DialogHeader>
          <DialogTitle className="font-display tracking-[0.1em] text-[#F5F3EF]" data-testid="apply-modal-title">
            {type ? `APPLY — ${label}` : "APPLY FOR CAPTAIN'S CUT"}
          </DialogTitle>
        </DialogHeader>

        {!type ? (
          <div data-testid="apply-type-step">
            <p className="font-editorial italic text-base text-[#94A3B8]">"Can you make the cut?" — choose your path.</p>
            <div className="grid sm:grid-cols-3 gap-3 mt-5">
              {TYPES.map(({ id, title, desc, icon: Icon }) => (
                <button key={id} onClick={() => pick(id)}
                  className="group text-left border border-[#D4AF37]/25 p-5 hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 transition-colors duration-300"
                  data-testid={`apply-type-${id}`}>
                  <Icon size={22} className="text-[#D4AF37] group-hover:scale-110 transition-transform duration-300" />
                  <h4 className="font-display text-sm tracking-[0.15em] text-[#F5F3EF] mt-4">{title}</h4>
                  <p className="text-xs text-[#94A3B8] mt-2 leading-relaxed">{desc}</p>
                </button>
              ))}
            </div>
            <p className="text-[11px] text-[#94A3B8]/70 mt-5 leading-relaxed">
              Every path requires email verification and acceptance of the application disclosure before any questions are shown.
            </p>
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <StepBar steps={STEPS} current={step} />
              <button onClick={back} className="flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase text-[#94A3B8] hover:text-[#E8C15A] transition-colors" data-testid="apply-change-path-btn">
                <ArrowLeft size={12} /> Change path
              </button>
            </div>
            <div className="mt-6">
              {step === 0 && (
                <VerifyEmail onVerified={signIn} purpose="application"
                  intro="Step 1 of 3 — verify your email. Your application progress is saved on this device while you verify." />
              )}
              {step === 1 && <Disclosure headers={headers} onAccepted={setMember} />}
              {step === 2 && (options ? <Form options={options} headers={headers} onCreated={created} /> : <p className="text-xs text-[#94A3B8]">Loading…</p>)}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
